const asArray = (value) => (Array.isArray(value) ? value : []);

const sourceName = (value) => {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';
  return value.sourceSystem
    || value.system
    || value.provider
    || value.source
    || value.name
    || '';
};

/**
 * Integration metadata is intentionally read from both the compact source list
 * and richer entity-link variants. This keeps the admin UI compatible while
 * the v2 API is rolled out independently of the frontend.
 */
export const integrationSourceSystems = (entity) => {
  if (!entity) return [];
  const values = [
    ...asArray(entity.integrationSourceSystems),
    ...asArray(entity.integrationLinks),
    ...asArray(entity.entityLinks),
  ];
  if (entity.externalSource) values.push(entity.externalSource);
  return [...new Set(values.map(sourceName).filter(Boolean))];
};

export const isCrmSynced = (entity) => integrationSourceSystems(entity)
  .some((source) => /(^|[^a-z])(vikar\s*crm|vikarcrm|crm)([^a-z]|$)/i.test(String(source)));

export const getPlacements = (entity) => {
  if (!entity) return [];
  if (Array.isArray(entity.placements)) return entity.placements;
  if (Array.isArray(entity.placementHistory)) return entity.placementHistory;
  return [];
};

const placementDateStr = (today) => (today instanceof Date
  ? `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  : String(today).slice(0, 10));

const placementCoversToday = (placement, date) => {
  const start = placement.startDate ? String(placement.startDate).slice(0, 10) : null;
  const end = placement.endDate ? String(placement.endDate).slice(0, 10) : null;
  return (!start || start <= date) && (!end || end >= date);
};

export const placementIsCurrent = (placement, today = new Date()) => {
  if (!placement) return false;
  const status = String(placement.status || '').toLowerCase();
  if (['ended', 'inactive', 'cancelled', 'canceled', 'closed'].includes(status)) return false;
  // An active fixed-term placement is stored 'active' until its end date has
  // passed (v1.27.0), so an active status alone no longer implies "running
  // today" — a future-dated start is planned, not current.
  return placementCoversToday(placement, placementDateStr(today));
};

// A placement that has not started yet: shown as "Planned" instead of its raw
// status, so a booking for next week is distinguishable from a running one.
export const placementIsPlanned = (placement, today = new Date()) => {
  if (!placement) return false;
  const status = String(placement.status || '').toLowerCase();
  if (['ended', 'inactive', 'cancelled', 'canceled', 'closed'].includes(status)) return false;
  const start = placement.startDate ? String(placement.startDate).slice(0, 10) : null;
  return Boolean(start && start > placementDateStr(today));
};

// The placement is over: either flipped to 'ended', or its end date has passed
// and the nightly lifecycle sweep simply has not run yet. Display code uses
// this so a just-expired placement never shows a raw stale 'active'.
export const placementHasEnded = (placement, today = new Date()) => {
  if (!placement) return false;
  const status = String(placement.status || '').toLowerCase();
  if (['ended', 'inactive', 'cancelled', 'canceled', 'closed'].includes(status)) return true;
  const end = placement.endDate ? String(placement.endDate).slice(0, 10) : null;
  return Boolean(end && end < placementDateStr(today));
};

export const formatPlacementDate = (value) => {
  if (!value) return 'Open';
  const raw = String(value).slice(0, 10);
  const parsed = new Date(`${raw}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? raw : parsed.toLocaleDateString();
};

export const placementCustomerName = (placement, customers = []) => {
  if (!placement) return 'Unknown customer';
  return placement.customerName
    || placement.customer?.name
    || placement.customer?.companyName
    || customers.find((customer) => Number(customer.id) === Number(placement.customerId))?.name
    || `Customer #${placement.customerId}`;
};

export const placementEmployeeName = (placement, employees = []) => {
  if (!placement) return 'Unknown employee';
  const embedded = placement.employee || {};
  const found = employees.find((employee) => (
    Number(employee.employeeId) === Number(placement.employeeId)
    || Number(employee.id) === Number(placement.employeeUserId)
  ));
  const firstName = embedded.firstName || found?.firstName || '';
  const lastName = embedded.lastName || found?.lastName || '';
  return placement.employeeName
    || embedded.name
    || `${firstName} ${lastName}`.trim()
    || `Employee #${placement.employeeId}`;
};

export const placementContextLabel = (placement) => {
  const context = placement?.sourceContext;
  if (!context) return 'Tempus';
  if (typeof context === 'string') return context;
  return context.label || context.name || context.source || context.workflow || 'Integration';
};

export const salaryItemsForRow = (row) => {
  if (!row) return [];
  if (Array.isArray(row.salaryItems)) return row.salaryItems;
  if (Array.isArray(row.allocations)) return row.allocations;
  return [];
};

export const salaryItemAmount = (item) => {
  if (item?.amountMinor != null) return Number(item.amountMinor) / 100;
  if (item?.amount != null) return Number(item.amount);
  return 0;
};

export const unallocatedAmountMinor = (row) => {
  if (row?.unallocatedAmountMinor != null) return Number(row.unallocatedAmountMinor) || 0;
  return salaryItemsForRow(row)
    .filter((item) => !item.placementId && !item.customerId)
    .reduce((total, item) => total + (Number(item.amountMinor) || 0), 0);
};
